import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Pencil, Loader2, Package } from "lucide-react";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";
import BetaNavbar from "./components/BetaNavbar";
import InfoField from "./components/Manufacturing/InfoField";
import BatchStatusBar from "./components/Manufacturing/BatchStatusBar";
import BatchFormModal from "./components/Manufacturing/BatchFormModal";
import WearableList from "./components/Manufacturing/WearableList";
import WearableFormModal from "./components/Manufacturing/WearableFormModal";
import {
  formatDate,
  formatApiError,
} from "./components/Manufacturing/constants";

const BatchDetail = () => {
  const { t } = useTranslation();
  const { batchId } = useParams();
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAllocate, setShowAllocate] = useState(false);
  const [listKey, setListKey] = useState(0);

  const fetchBatch = useCallback(async () => {
    try {
      const res = await betaAPI.getBatch({ batchId });
      setBatch(res.data);
    } catch (err) {
      if (err?.status === 404) {
        setNotFound(true);
      } else {
        showToast.error(formatApiError(err, t("admin.batchDetail.loadFailed")));
      }
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    fetchBatch();
  }, [fetchBatch]);

  const handleAllocated = () => {
    fetchBatch();
    setListKey((k) => k + 1); // remount the list to refetch
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <BetaNavbar hideWorkspaceSelector />
        <div className="flex items-center justify-center py-20">
          <Loader2 size={22} className="animate-spin text-[#E07A3A]" />
        </div>
      </div>
    );
  }

  if (notFound || !batch) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <BetaNavbar hideWorkspaceSelector />
        <div className="mx-auto max-w-[1440px] px-6 py-20 text-center">
          <p className="text-sm text-[#6B7280]">
            {t("admin.batchDetail.notFound")}
          </p>
          <button
            onClick={() => navigate("/v2/admin/manufacturing")}
            className="mt-4 text-sm font-medium text-[#E07A3A]"
          >
            {t("admin.batchDetail.backToManufacturing")}
          </button>
        </div>
      </div>
    );
  }

  const fillPct = batch.planned_quantity
    ? Math.min(
        100,
        Math.round(((batch.wearable_count ?? 0) / batch.planned_quantity) * 100)
      )
    : 0;

  return (
    <div className="min-h-screen overscroll-none bg-[#F8F9FA]">
      <BetaNavbar hideWorkspaceSelector />

      <main>
        {/* Hero */}
        <div
          style={{
            background:
              "linear-gradient(180deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 50%, rgba(255, 255, 255, 0) 100%)",
          }}
        >
          <div className="mx-auto max-w-[1440px] px-6 pb-6 pt-5">
            <button
              onClick={() => navigate("/v2/admin/manufacturing")}
              className="mb-3 flex items-center gap-1 text-xs font-medium text-[#6B7280] transition-colors duration-150 hover:text-[#E07A3A]"
            >
              <ChevronLeft size={14} />
              {t("admin.batchDetail.breadcrumb")}
            </button>

            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm">
                  <Package size={24} className="text-[#E07A3A]" />
                </div>
                <div>
                  <h1 className="text-[26px] font-bold leading-tight text-[#111827]">
                    {batch.code}
                  </h1>
                  <p className="mt-0.5 text-xs text-[#6B7280]">
                    {t("admin.batchDetail.summary", {
                      manufacturer:
                        batch.cm_name || t("admin.batchDetail.noManufacturer"),
                      allocated: (batch.wearable_count ?? 0).toLocaleString(),
                      planned: (batch.planned_quantity ?? 0).toLocaleString(),
                      percent: fillPct,
                    })}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowEdit(true)}
                className="flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2.5 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
              >
                <Pencil size={14} />
                {t("common.actions.edit")}
              </button>
            </div>
          </div>
        </div>

        <div className="mx-auto grid max-w-[1440px] gap-4 px-6 pb-6 lg:grid-cols-3">
          {/* Left column: metadata + status rollup */}
          <div className="flex flex-col gap-4 lg:col-span-1">
            <div className="rounded-xl border border-[#F3F4F6] bg-white p-5">
              <h2 className="mb-4 text-sm font-semibold text-[#111827]">
                {t("admin.batchDetail.details")}
              </h2>
              <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                <InfoField
                  label={t("admin.batchDetail.hwRevision")}
                  value={batch.hw_revision}
                />
                <InfoField
                  label={t("admin.batchDetail.pcbaRevision")}
                  value={batch.pcba_revision}
                />
                <InfoField
                  label={t("admin.batchDetail.plannedQty")}
                  value={batch.planned_quantity?.toLocaleString()}
                />
                <InfoField
                  label={t("admin.batchDetail.allocated")}
                  value={batch.wearable_count?.toLocaleString()}
                />
                <InfoField
                  label={t("admin.batchDetail.mfgStart")}
                  value={formatDate(batch.manufacture_start_date)}
                />
                <InfoField
                  label={t("admin.batchDetail.mfgEnd")}
                  value={formatDate(batch.manufacture_end_date)}
                />
                <InfoField
                  className="col-span-2"
                  label={t("admin.batchDetail.created")}
                  value={formatDate(batch.created_at)}
                />
                {batch.notes && (
                  <InfoField
                    className="col-span-2"
                    label={t("admin.batchDetail.notes")}
                    value={batch.notes}
                  />
                )}
              </div>
            </div>

            <div className="rounded-xl border border-[#F3F4F6] bg-white p-5">
              <h2 className="mb-4 text-sm font-semibold text-[#111827]">
                {t("admin.batchDetail.statusBreakdown")}
              </h2>
              <BatchStatusBar statusCounts={batch.status_counts} />
            </div>
          </div>

          {/* Right column: wearables */}
          <div className="lg:col-span-2">
            <WearableList
              key={listKey}
              batchId={batch.id}
              onAllocate={() => setShowAllocate(true)}
            />
          </div>
        </div>
      </main>

      <BatchFormModal
        show={showEdit}
        onClose={() => setShowEdit(false)}
        batch={batch}
        onSaved={(updated) => setBatch((prev) => ({ ...prev, ...updated }))}
      />
      <WearableFormModal
        show={showAllocate}
        onClose={() => setShowAllocate(false)}
        defaultBatchId={batch.id}
        onSaved={handleAllocated}
      />
    </div>
  );
};

export default BatchDetail;
